import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Button, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import CustomDrawer from '../../components/Drawer/Drawer';
import TabHeader from '../../components/TabHeader/TabHeader';
import { LANG_TEXT } from '../../constants/language';
import { useOrderContext } from '../../context/OrderContext';
import usePermission from '../../hooks/usePermission';
import { usePreorderEnabled } from '../../hooks/usePreorderEnabled';
import { CustomTabProps } from '../../interfaces/index.types';
import DrawerOrderItem from './components/DrawerOrderItem/DrawerOrderItem';
import { ListOrder } from './components/ListOrder/ListOrder';
import PreOrdersList from './components/PreOrdersList/PreOrdersList';
import { IOrders } from './order.types';
import { useStyles } from './styles';
export default function Dashboard() {
  const classes = useStyles();
  const { allOrdersOfDay, newOrdersOfDay, readyOrdersOfDay, deliveredOrdersOfDay, deliveringOrdersOfDay, cancelledOrdersOfDay, preOrdersOfDay, loading } =
    useOrderContext();
  const [value, setValue] = useState(0);
  const { t } = useTranslation();
  const [selectedOrder, setSelectedOrder] = useState<IOrders>();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showPreOrder, setShowPreOrder] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = searchParams.get('id');
  const { isAdminActionBlocked } = usePermission();
  const { enabledNewVersion } = usePreorderEnabled();

  const handleChange = (newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (orderId && allOrdersOfDay) {
      const selected = allOrdersOfDay.find((item) => item.id === orderId);
      if (selected) {
        setOpenDrawer(true);
        setSelectedOrder(selected);
      }
    }
  }, [orderId, allOrdersOfDay]);

  useEffect(() => {
    if (!openDrawer && orderId) {
      setSearchParams((prev) => {
        prev.delete('id');
        return prev;
      });
    }
  }, [openDrawer, orderId, setSearchParams]);

  const toggleDrawer = (open: boolean) => {
    setOpenDrawer(open);
  };

  const tabs: CustomTabProps[] = useMemo(() => {
    return [
      { label: t(LANG_TEXT.ALL), id: 0, value: value },
      { label: t(LANG_TEXT.NEW), total: newOrdersOfDay.length, id: 1, value: value },
      { label: t(LANG_TEXT.READY_FOR_PICK_UP), total: readyOrdersOfDay.length, id: 2, value: value },
      { label: t(LANG_TEXT.DELIVERING), total: deliveringOrdersOfDay.length, id: 3, value: value },
      { label: t(LANG_TEXT.DELIVERED), total: deliveredOrdersOfDay.length, id: 4, value: value },
      { label: t(LANG_TEXT.CANCELLED), total: cancelledOrdersOfDay.length, id: 5, value: value },
    ];
  }, [allOrdersOfDay?.length, newOrdersOfDay?.length, readyOrdersOfDay?.length, deliveringOrdersOfDay?.length, deliveredOrdersOfDay?.length, t, value]);

  return (
    <Box className={classes.Dashboard}>
      <Box
        className={classes.Order}
        sx={{
          backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]),
        }}
      >
        <Typography fontSize={24} fontWeight={500} lineHeight={'32px'} className={classes.Title}>
          {t(LANG_TEXT.DASHBOARD)}
        </Typography>

        <Box className={classes.TabPanel}>
          <TabHeader tabs={tabs} handleChange={handleChange} value={value} />
          <Box role="tabpanel" hidden={value !== 0} id={`simple-tabpanel-${0}`} aria-labelledby={`simple-tab-${0}`}>
            <ListOrder
              key={0}
              index={0}
              value={value}
              orderList={allOrdersOfDay}
              loading={loading}
              toggleDrawer={toggleDrawer}
              openDrawer={openDrawer}
              showPreOrder={showPreOrder}
              setSelectedOrder={setSelectedOrder}
            />
          </Box>
          <Box role="tabpanel" hidden={value !== 1} id={`simple-tabpanel-${1}`} aria-labelledby={`simple-tab-${1}`}>
            <ListOrder
              key={1}
              index={1}
              value={value}
              orderList={newOrdersOfDay}
              loading={loading}
              toggleDrawer={toggleDrawer}
              openDrawer={openDrawer}
              showPreOrder={showPreOrder}
              setSelectedOrder={setSelectedOrder}
            />
          </Box>
          <Box role="tabpanel" hidden={value !== 2} id={`simple-tabpanel-${2}`} aria-labelledby={`simple-tab-${2}`}>
            <ListOrder
              key={2}
              index={2}
              value={value}
              orderList={readyOrdersOfDay}
              loading={loading}
              toggleDrawer={toggleDrawer}
              openDrawer={openDrawer}
              showPreOrder={showPreOrder}
              setSelectedOrder={setSelectedOrder}
            />
          </Box>
          <Box role="tabpanel" hidden={value !== 3} id={`simple-tabpanel-${3}`} aria-labelledby={`simple-tab-${3}`}>
            <ListOrder
              key={3}
              index={3}
              value={value}
              orderList={deliveringOrdersOfDay}
              loading={loading}
              toggleDrawer={toggleDrawer}
              openDrawer={openDrawer}
              showPreOrder={showPreOrder}
              setSelectedOrder={setSelectedOrder}
            />
          </Box>
          <Box role="tabpanel" hidden={value !== 4} id={`simple-tabpanel-${4}`} aria-labelledby={`simple-tab-${4}`}>
            <ListOrder
              key={4}
              index={4}
              value={value}
              orderList={deliveredOrdersOfDay}
              loading={loading}
              toggleDrawer={toggleDrawer}
              openDrawer={openDrawer}
              showPreOrder={showPreOrder}
              setSelectedOrder={setSelectedOrder}
            />
          </Box>
          <Box role="tabpanel" hidden={value !== 5} id={`simple-tabpanel-${5}`} aria-labelledby={`simple-tab-${5}`}>
            <ListOrder
              key={5}
              index={5}
              value={value}
              orderList={cancelledOrdersOfDay}
              loading={loading}
              toggleDrawer={toggleDrawer}
              openDrawer={openDrawer}
              showPreOrder={showPreOrder}
              setSelectedOrder={setSelectedOrder}
            />
          </Box>
          <CustomDrawer openDrawer={openDrawer} toggleDrawer={toggleDrawer} DrawerItem={<DrawerOrderItem order={selectedOrder} toggleDrawer={toggleDrawer} />} />
        </Box>
      </Box>

      {enabledNewVersion && (
        <Box
          className={showPreOrder ? classes.PreOrderShow : classes.PreOrderHide}
          sx={{
            minWidth: isAdminActionBlocked && showPreOrder ? 290 : showPreOrder ? 320 : 10,
            width: isAdminActionBlocked && showPreOrder ? 290 : showPreOrder ? 320 : 10,
          }}
        >
          <Button size="small" variant="contained" onClick={() => setShowPreOrder(!showPreOrder)} className={classes.PreOrderToggleButton}>
            <ArrowForwardIcon sx={{ color: '#1D2939' }} />
          </Button>
          <Box className={showPreOrder ? classes.PreOrderContentShow : classes.PreOrderContentHide}>
            <Typography fontSize={24} fontWeight={500} lineHeight={'32px'}>
              {t(LANG_TEXT.PRE_ORDERS)}
            </Typography>
            <PreOrdersList orders={preOrdersOfDay || []} loading={loading} toggleDrawer={toggleDrawer} openDrawer={openDrawer} setSelectedOrder={setSelectedOrder} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
